import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PawPrint, Check, ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Onboarding() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState(user?.tenant?.name || "");
  const [types, setTypes] = useState([]);
  const [branch, setBranch] = useState({ name: "Main Branch", city: "Bengaluru" });
  const [busy, setBusy] = useState(false);

  const { data: meta } = useQuery({
    queryKey: ["business-types"],
    queryFn: async () => (await api.get("/meta/business-types")).data,
  });

  const toggle = (id) => setTypes((t) => (t.includes(id) ? t.filter((x) => x !== id) : [...t, id]));

  // Server-authoritative gate: an unpaid paid-signup can never reach onboarding.
  if (user?.signup_payment_required) return <Navigate to="/checkout" replace />;

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/tenants/me", { name, business_types: types, onboarded: true });
      if (branch.name) await api.post("/branches", { ...branch, is_primary: true });
      await refreshMe();
      toast.success("Welcome to PAWKT! 🐾");
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="border-b border-slate-200 bg-white px-6 h-16 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center"><PawPrint className="h-5 w-5 text-emerald-400" /></div>
        <span className="font-display text-lg font-extrabold tracking-tight">PAWKT</span>
        <span className="ml-auto text-sm text-slate-400">Step {step} of 3</span>
      </div>

      <div className="flex-1 flex items-start justify-center p-6 sm:p-12">
        <div className="w-full max-w-2xl">
          <div className="flex gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className={cn("h-1.5 flex-1 rounded-full", s <= step ? "bg-emerald-500" : "bg-slate-200")} />
            ))}
          </div>

          {step === 1 && (
            <div className="animate-fade-in">
              <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Name your business</h1>
              <p className="text-slate-500 mt-1">This appears on invoices and your customer portal.</p>
              <input data-testid="onb-business-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Paws Pet Care"
                className="mt-6 w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-lg outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10" />
              <button data-testid="onb-next-1" disabled={!name.trim()} onClick={() => setStep(2)}
                className="mt-6 flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="animate-fade-in">
              <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">What do you offer?</h1>
              <p className="text-slate-500 mt-1">Select all that apply — we'll switch on the right modules.</p>
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {(meta?.business_types || []).map((bt) => (
                  <button key={bt.id} data-testid={`onb-type-${bt.id}`} onClick={() => toggle(bt.id)}
                    className={cn("flex items-center justify-between rounded-lg border px-3.5 py-3 text-sm font-medium text-left transition-all",
                      types.includes(bt.id) ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300")}>
                    {bt.label}
                    {types.includes(bt.id) && <Check className="h-4 w-4 text-emerald-600" />}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex gap-2">
                <button onClick={() => setStep(1)} className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-medium hover:bg-slate-50">Back</button>
                <button data-testid="onb-next-2" disabled={types.length === 0} onClick={() => setStep(3)}
                  className="flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-fade-in">
              <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Your first branch</h1>
              <p className="text-slate-500 mt-1">You can add more branches later from Settings.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <input data-testid="onb-branch-name" value={branch.name} onChange={(e) => setBranch({ ...branch, name: e.target.value })} placeholder="Branch name"
                  className="rounded-lg border border-slate-200 bg-white px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10" />
                <input data-testid="onb-branch-city" value={branch.city} onChange={(e) => setBranch({ ...branch, city: e.target.value })} placeholder="City"
                  className="rounded-lg border border-slate-200 bg-white px-4 py-3 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10" />
              </div>
              <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
                <Sparkles className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-800">You're all set. PAWKT will open with your live dashboard — add customers, pets and bookings right away.</p>
              </div>
              <div className="mt-6 flex gap-2">
                <button onClick={() => setStep(2)} className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-medium hover:bg-slate-50">Back</button>
                <button data-testid="onb-finish" disabled={busy} onClick={finish}
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                  {busy ? "Setting up…" : "Launch PAWKT"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
